/* ============================================
   DRIFT Theme by WEBEXP, LLC
   DRIFT Site Background — Gradient Initialization
   Loaded by drift-background.liquid when the site-wide animated gradient
   is enabled in theme settings. Initializes drift-bg-shader.js on the
   site-level canvas using the theme's light/dark seed colors. The DRIFT
   Product Feed section can override the palette via window.driftBgShader
   .setColors() when per-product metafields are set.
   https://webexp.dev
   ============================================ */
(function () {
  'use strict';

  var bg = document.querySelector('[data-drift-site-bg]');
  if (!bg) return;
  var canvas = bg.querySelector('[data-drift-site-bg-canvas]');
  if (!canvas) return;

  /* === HSL helpers (mirror the section-level derivation in drift-feed.js) === */
  function parseHex(hex) {
    if (!hex) return null;
    hex = hex.trim().replace(/^#/, '');
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    if (hex.length !== 6) return null;
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16)
    };
  }
  function rgbToHsl(rgb) {
    var r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h *= 60;
    }
    return { h: h, s: s * 100, l: l * 100 };
  }
  function hslToHex(h, s, l) {
    h = ((h % 360) + 360) % 360;
    s = Math.max(0, Math.min(100, s)) / 100;
    l = Math.max(0, Math.min(100, l)) / 100;
    var c = (1 - Math.abs(2 * l - 1)) * s;
    var x = c * (1 - Math.abs((h / 60) % 2 - 1));
    var m = l - c / 2;
    var r = 0, g = 0, b = 0;
    if (h < 60)       { r = c; g = x; b = 0; }
    else if (h < 120) { r = x; g = c; b = 0; }
    else if (h < 180) { r = 0; g = c; b = x; }
    else if (h < 240) { r = 0; g = x; b = c; }
    else if (h < 300) { r = x; g = 0; b = c; }
    else              { r = c; g = 0; b = x; }
    var toHex = function (v) {
      var n = Math.round((v + m) * 255).toString(16);
      return n.length === 1 ? '0' + n : n;
    };
    return '#' + toHex(r) + toHex(g) + toHex(b);
  }
  function derivePalette(seedHex) {
    var rgb = parseHex(seedHex);
    if (!rgb) return null;
    var hsl = rgbToHsl(rgb);
    var h = hsl.h, s = hsl.s, l = hsl.l;
    if (s < 8) {
      return [
        hslToHex(0, 0, Math.min(96, Math.max(20, l + 30))),
        hslToHex(0, 0, l),
        hslToHex(0, 0, Math.min(88, Math.max(8, l - 12))),
        hslToHex(0, 0, Math.min(75, Math.max(4, l - 28)))
      ];
    }
    s = Math.max(25, s);
    return [
      hslToHex(h, Math.max(15, s - 5), Math.min(94, l + 28)),
      hslToHex(h, s, l),
      hslToHex((h + 35) % 360, Math.min(95, s + 10), Math.max(20, l - 8)),
      hslToHex(h, Math.min(95, s + 8), Math.max(8, l - 32))
    ];
  }

  /* Pick the seed for the current mode. */
  function currentSeed() {
    var mode = document.documentElement.getAttribute('data-drift-mode') || 'dark';
    return mode === 'light'
      ? bg.getAttribute('data-gradient-light')
      : bg.getAttribute('data-gradient-dark');
  }

  /* The "target palette" is the palette we WANT to display right now. It's
     updated by override/applyFromSeed/reset. It's also what bootShader uses
     to init — meaning if drift-feed.js or the PDP script call override
     BEFORE the shader script has loaded, the shader still inits with the
     correct palette (no flash to theme defaults then snap to metafield). */
  var targetPalette = null;

  function deriveThemeDefaults() {
    var seed = currentSeed();
    return seed ? derivePalette(seed) : null;
  }

  function applyThemeDefaults() {
    targetPalette = deriveThemeDefaults();
    if (targetPalette && window.driftBgShader) {
      window.driftBgShader.setColors(targetPalette);
    }
  }

  /* Expose a global API used by:
       - drift-feed.js: per-product overrides as the feed swipes
       - PDP / other pages: push the active product's metafield color
     Both update targetPalette so the shader picks it up whenever it loads. */
  window.driftSiteBg = {
    override: function (palette) {
      if (!palette) return;
      targetPalette = palette;
      if (window.driftBgShader) window.driftBgShader.setColors(palette);
    },
    applyFromSeed: function (seedHex) {
      var palette = derivePalette(seedHex);
      if (palette) this.override(palette);
    },
    reset: applyThemeDefaults
  };

  /* Signal that the API is available so any code that loaded earlier (e.g.
     drift-feed.js which initialized before this script finished) can push
     a deferred palette now. drift-feed.js listens for this event and re-
     applies the current slide's bg if it wasn't applied initially. */
  try {
    document.dispatchEvent(new CustomEvent('driftSiteBg:ready'));
  } catch (e) {}

  /* Lazy-load the shader, init with whatever target palette is current at
     the time the shader actually loads. This handles the race where
     drift-feed.js / PDP script call override() before the shader is ready
     — by the time init runs, targetPalette has the metafield-derived
     palette, so the very first paint already shows the right colors. */
  function bootShader() {
    if (!targetPalette) targetPalette = deriveThemeDefaults() || ['#222', '#444', '#666', '#888'];
    if (window.driftBgShader && window.driftBgShader.init(canvas, targetPalette)) return;
    var src = bg.getAttribute('data-bg-shader-src');
    if (!src) return;
    var s = document.createElement('script');
    s.src = src;
    s.onload = function () {
      if (window.driftBgShader) {
        /* Use the CURRENT targetPalette at load time, not the one captured
           when bootShader was called. Override calls between then and now
           are reflected. */
        window.driftBgShader.init(canvas, targetPalette);
      }
    };
    document.head.appendChild(s);
  }
  bootShader();

  /* Re-derive the palette when the user toggles light/dark mode. */
  var modeObs = new MutationObserver(function () { applyThemeDefaults(); });
  modeObs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-drift-mode'] });
})();
